// src/pages/PaymentList.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../utils/config"; // keep your original import path
import { FiDownload } from "react-icons/fi";
import { generatePaymentReceiptPDF } from "./pdfReceipt";

export default function PaymentList() {
  const token = localStorage.getItem("token");

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // UI state
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("all"); // all | cash | online | cheque | upi | card
  const [from, setFrom] = useState(""); // yyyy-mm-dd
  const [to, setTo] = useState("");
  const [customer, setCustomer] = useState("all"); // 'all' or exact customer name

  const fmtINR = (n) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(`${API_BASE_URL}/api/payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Normalize → newest first
      const rows = (res.data || [])
        .map((p) => {
          const d = p.payment_date ? new Date(p.payment_date) : null;
          return {
            id: p.id,
            customer_name: p.customer_name || "-",
            amount: Number(p.amount || 0),
            payment_mode: (p.payment_mode || "").toLowerCase(),
            reference_no: p.reference_no || "",
            voucher_no: p.voucher_no || "", // using this as "Receipt #"
            remarks: p.remarks || "",
            payment_date: d,
            payment_date_str: d ? d.toISOString().slice(0, 10) : "-", // for PDF
          };
        })
        .sort(
          (a, b) =>
            (b.payment_date?.getTime?.() || 0) - (a.payment_date?.getTime?.() || 0)
        );
      setPayments(rows);
    } catch (e) {
      console.error("Error fetching payments:", e);
      setError("Failed to load payments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Distinct customers for dropdown (optional – kept ready)
  const customerOptions = useMemo(() => {
    const set = new Set();
    payments.forEach((p) => {
      const name = (p.customer_name || "-").trim();
      if (name) set.add(name);
    });
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [payments]);

  // map some aliases to keep it friendly
  const MODE_ALIASES = {
    upi: ["upi", "gpay", "phonepe", "paytm"],
    card: ["card", "debit", "credit"],
    cash: ["cash"],
    online: ["online", "neft", "rtgs", "imps", "netbanking"],
    cheque: ["cheque", "check"],
  };

  // ------ Filters ------
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const fromTs = from ? new Date(from).setHours(0, 0, 0, 0) : null;
    const toTs = to ? new Date(to).setHours(23, 59, 59, 999) : null;

    return payments.filter((p) => {
      // by mode
      if (mode !== "all") {
        const allowed = MODE_ALIASES[mode] || [mode];
        const m = p.payment_mode;
        const hit = allowed.some((x) => m.includes(x));
        if (!hit) return false;
      }
      // by customer (dropdown optional)
      if (customer !== "all" && p.customer_name !== customer) return false;

      // search
      if (term) {
        const ref = (p.reference_no || "").toLowerCase();
        if (
          !p.customer_name.toLowerCase().includes(term) &&
          !p.payment_mode.toLowerCase().includes(term) &&
          !ref.includes(term) &&
          !String(p.voucher_no || "").toLowerCase().includes(term)
        ) {
          return false;
        }
      }
      // date range
      const ts = p.payment_date?.getTime?.() || 0;
      if (fromTs && ts < fromTs) return false;
      if (toTs && ts > toTs) return false;

      return true;
    });
  }, [payments, q, mode, from, to, customer]);

  // Totals
  const totalAmount = useMemo(
    () => filtered.reduce((s, r) => s + Number(r.amount || 0), 0),
    [filtered]
  );
  const avgAmount = useMemo(
    () => (filtered.length ? totalAmount / filtered.length : 0),
    [filtered.length, totalAmount]
  );

  // Totals by mode (over filtered rows)
  const totalsByMode = useMemo(() => {
    const t = { cash: 0, online: 0, cheque: 0, upi: 0, card: 0 };
    filtered.forEach((p) => {
      const m = p.payment_mode;
      if (MODE_ALIASES.cash.some((x) => m.includes(x))) t.cash += p.amount;
      else if (MODE_ALIASES.online.some((x) => m.includes(x))) t.online += p.amount;
      else if (MODE_ALIASES.cheque.some((x) => m.includes(x))) t.cheque += p.amount;
      else if (MODE_ALIASES.upi.some((x) => m.includes(x))) t.upi += p.amount;
      else if (MODE_ALIASES.card.some((x) => m.includes(x))) t.card += p.amount;
    });
    return t;
  }, [filtered]);

  const ModePill = ({ value, label }) => (
    <button
      onClick={() => setMode(value)}
      className={`rounded-full px-3 py-1 text-xs font-medium transition ${
        mode === value
          ? "bg-blue-600 text-white"
          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
      }`}
    >
      {label}
    </button>
  );

  // Quick date ranges
  const setLastNDays = (n) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (n - 1));
    const toStr = end.toISOString().slice(0, 10);
    const fromStr = start.toISOString().slice(0, 10);
    setFrom(fromStr);
    setTo(toStr);
  };
  const clearFilters = () => {
    setQ("");
    setMode("all");
    setFrom("");
    setTo("");
    setCustomer("all");
  };

  // --- PDF download handler (maps your fields → PDF util input)
  const onDownload = (p) => {
    const company = {
      name: "WECARE INDIA Agency",
      address: "D20 New Bapu Nagar, Bhilwara, Rajasthan",
      gstin: "08AAACW0000A1Z5",
      phone: "+91 880009753",
      email: "bhl.wecareindia@gmail.com",
    };

    generatePaymentReceiptPDF(
      {
        receipt_no: p.voucher_no || "-",       // mapping
        payment_date: p.payment_date_str || "-",
        customer_name: p.customer_name || "-",
        customer_gst: "",                      // set if you have it in API
        mode: p.payment_mode || "-",
        reference_no: p.reference_no || "-",
        narration: p.remarks || "",
        amount: p.amount || 0,
        settlements: [],                       // pass if you have invoice settlements
      },
      company
    );
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="mx-auto mb-4 flex max-w-6xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Payment List
        </h1>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search receipt / customer / mode / reference…"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:w-80"
          />
          <Link
            to="/payments/add"
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 active:scale-[.98]"
          >
            + Add Payment
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="mx-auto mb-3 max-w-6xl">
        <div className="flex flex-wrap items-center gap-2">
          <ModePill value="all" label="All" />
          <ModePill value="cash" label="Cash" />
          <ModePill value="online" label="Online" />
          <ModePill value="upi" label="UPI" />
          <ModePill value="cheque" label="Cheque" />
          <ModePill value="card" label="Card" />

          {/* Optional Customer dropdown */}
          {/* <select
            value={customer}
            onChange={(e) => setCustomer(e.target.value)}
            className="ml-1 rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {customerOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select> */}

          {/* Date range + quick ranges */}
          <div className="ml-auto flex items-center gap-2">
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <span className="text-slate-500 text-sm">to</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => setLastNDays(7)}
              className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
            >
              Last 7d
            </button>
            <button
              type="button"
              onClick={() => setLastNDays(15)}
              className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
            >
              15d
            </button>
            <button
              type="button"
              onClick={() => setLastNDays(30)}
              className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
            >
              30d
            </button>
            <button
              type="button"
              onClick={() => setLastNDays(90)}
              className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
            >
              90d
            </button>
            <button
              type="button"
              onClick={clearFilters}
              className="ml-1 rounded-full border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {!loading && !error && (
        <div className="mx-auto mb-4 grid max-w-6xl grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs text-slate-500">Payments</div>
            <div className="mt-1 text-2xl font-semibold">{filtered.length}</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs text-slate-500">Total Amount</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">
              {fmtINR(totalAmount)}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs text-slate-500">Avg Payment</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">
              {fmtINR(avgAmount)}
            </div>
          </div>
        </div>
      )}

      {/* Totals by Mode */}
      {!loading && !error && (
        <div className="mx-auto mb-3 flex max-w-6xl flex-wrap items-center gap-2">
          {[
            ["cash", "Cash"],
            ["online", "Online"],
            ["cheque", "Cheque"],
            ["upi", "UPI"],
            ["card", "Card"],
          ].map(([key, label]) => (
            <div
              key={key}
              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs shadow-sm"
            >
              <span className="text-slate-500">{label}:</span>{" "}
              <span className="font-medium">{fmtINR(totalsByMode[key])}</span>
            </div>
          ))}
        </div>
      )}

      {/* Status banners */}
      {error && (
        <div className="mx-auto mb-3 max-w-6xl rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}
      {loading && (
        <div className="mx-auto max-w-6xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          Loading…
        </div>
      )}

      {/* Data */}
      {!loading && (
        <div className="mx-auto max-w-6xl rounded-2xl border border-slate-200 bg-white shadow-sm">
          {/* Tiny header row */}
          <div className="flex items-center justify-between px-4 py-2 text-xs text-slate-500">
            <div>Total: {filtered.length}</div>
            <div className="tabular-nums">Amount: {fmtINR(totalAmount)}</div>
          </div>

          {/* Desktop table */}
          <div className="hidden overflow-x-auto sm:block">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-700">
                  <th className="px-3 py-2 text-left font-medium">Receipt #</th>
                  <th className="px-3 py-2 text-left font-medium">Customer</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                  <th className="px-3 py-2 text-left font-medium">Mode</th>
                  <th className="px-3 py-2 text-left font-medium">Reference</th>
                  <th className="px-3 py-2 text-left font-medium">Payment Date</th>
                  <th className="px-3 py-2 text-left font-medium">Remarks</th>
                  <th className="px-3 py-2 text-left font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="[&>tr:nth-child(even)]:bg-slate-50">
                {filtered.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="px-3 py-2">{p.voucher_no || "—"}</td>
                    <td className="px-3 py-2">{p.customer_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fmtINR(p.amount)}
                    </td>
                    <td className="px-3 py-2 capitalize">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">
                        {p.payment_mode || "-"}
                      </span>
                    </td>
                    <td className="px-3 py-2">{p.reference_no || "—"}</td>
                    <td className="px-3 py-2">
                      {p.payment_date ? p.payment_date.toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2">{p.remarks || "—"}</td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => onDownload(p)}
                        className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                        title="Download Receipt PDF"
                      >
                        <FiDownload /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-slate-500">
                      No matching payments found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="divide-y divide-slate-100 sm:hidden">
            {filtered.length === 0 && (
              <div className="p-4 text-center text-sm text-slate-500">
                No payments found.
              </div>
            )}
            {filtered.map((p) => (
              <div key={p.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-[72px] tabular-nums font-semibold">
                    {p.voucher_no || "—"}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">{p.customer_name}</div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {p.payment_date ? p.payment_date.toLocaleDateString() : "—"}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 capitalize">
                        {p.payment_mode || "-"}
                      </span>
                      {p.reference_no && (
                        <span className="truncate text-slate-600">
                          • {p.reference_no}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {p.remarks ? `Remarks: ${p.remarks}` : "—"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="tabular-nums font-semibold">
                      {fmtINR(p.amount)}
                    </div>
                    <button
                      onClick={() => onDownload(p)}
                      className="mt-2 inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                      title="Download Receipt PDF"
                    >
                      <FiDownload /> PDF
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
